import { buildSiteCatalog, resolveCanonicalSiteIdentity } from './siteIdentity';

const toArray = (value) => (Array.isArray(value) ? value : []);
const normalize = (value) => String(value || '').trim().toLowerCase();

const customerIdOf = (customer = {}) => String(customer.id || customer.customer_id || '').trim();
const customerCodeOf = (customer = {}) => String(customer.customer_id || customer.id || '-').trim();
const customerNameOf = (customer = {}) => String(customer.name || customer.customer_name || customerCodeOf(customer) || '-').trim();
const customerStatusRaw = (customer = {}) => String(customer.status || customer.connection_status || customer.monitoring_status || customer.status_detail?.connection_status || 'unknown').trim();
const customerSiteOf = (customer = {}, siteCatalog = null) => {
  const resolved = resolveCanonicalSiteIdentity(customer, siteCatalog);
  return String(resolved.site_id || '-').trim();
};
const linkedOdpIdOf = (customer = {}) => String(customer.linked_odp_id || customer.odp_id || '').trim();
const linkedOltIdOf = (customer = {}) => String(customer.linked_olt_id || customer.olt_id || '').trim();
const linkedRouterIdOf = (customer = {}) => String(customer.router_id || customer.nas_id || '').trim();

const activeTicketStatus = new Set(['open', 'investigating', 'mitigated']);
const inactiveIncidentStatus = new Set(['mitigated', 'closed', 'resolved']);
const healthyCustomerStates = new Set(['online', 'active', 'ok', 'healthy']);

const classifyCustomerState = (statusValue) => {
  const status = normalize(statusValue);
  if (!status) return 'unknown';
  if (['offline', 'down', 'terminated', 'critical', 'error'].includes(status)) return 'offline';
  if (['degraded', 'unstable', 'suspended'].includes(status)) return 'degraded';
  if (['warning', 'warn', 'maintenance', 'pending'].includes(status)) return 'warning';
  if (healthyCustomerStates.has(status)) return 'healthy';
  return 'unknown';
};

const parseEntityRef = (value) => {
  const [rawType, rawId] = String(value || '').split(':');
  const type = normalize(rawType);
  const id = String(rawId || '').trim();
  if (!type || !id) return null;
  return { type, id };
};

const sortByName = (items = []) => items.slice().sort((a, b) => String(a || '').localeCompare(String(b || '')));

const buildIndexes = ({ customers = [], odps = [], olts = [], nasRouters = [], siteCatalog = null }) => {
  const customersById = new Map();
  const customersByOdp = new Map();
  const customersByOlt = new Map();
  const customersByRouter = new Map();
  const customersBySite = new Map();
  const customerSiteById = new Map();
  const odpById = new Map();
  const oltById = new Map();
  const routersById = new Map();
  const routersBySite = new Map();

  const pushMapList = (map, key, value) => {
    const token = String(key || '').trim();
    if (!token) return;
    if (!map.has(token)) map.set(token, []);
    map.get(token).push(value);
  };

  toArray(odps).forEach((odp) => {
    const id = String(odp?.id || odp?.odp_id || '').trim();
    if (!id) return;
    odpById.set(id, odp);
  });

  toArray(olts).forEach((olt) => {
    const id = String(olt?.id || olt?.olt_id || '').trim();
    if (!id) return;
    oltById.set(id, olt);
  });

  toArray(nasRouters).forEach((router) => {
    const id = String(router?.id || router?.router_id || '').trim();
    if (!id) return;
    routersById.set(id, router);
    const siteIdentity = resolveCanonicalSiteIdentity(router, siteCatalog);
    const siteId = String(siteIdentity.site_id || '').trim();
    if (siteId) pushMapList(routersBySite, siteId, router);
  });

  toArray(customers).forEach((customer) => {
    const customerId = customerIdOf(customer);
    if (!customerId) return;
    customersById.set(customerId, customer);
    pushMapList(customersByOdp, linkedOdpIdOf(customer), customer);
    pushMapList(customersByOlt, linkedOltIdOf(customer), customer);
    pushMapList(customersByRouter, linkedRouterIdOf(customer), customer);

    const odp = odpById.get(linkedOdpIdOf(customer));
    const olt = oltById.get(linkedOltIdOf(customer) || String(odp?.olt_id || '').trim());
    const site = customerSiteOf(customer, siteCatalog) || String(odp?.site_id || olt?.site_id || '').trim();
    if (site) {
      pushMapList(customersBySite, site, customer);
      customerSiteById.set(customerId, site);
    }
  });

  return {
    customersById,
    customersByOdp,
    customersByOlt,
    customersByRouter,
    customersBySite,
    customerSiteById,
    odpById,
    oltById,
    routersById,
    routersBySite,
  };
};

const addCustomersFromEntity = ({ entity, indexes, customerIds }) => {
  if (!entity) return;
  const { type, id } = entity;
  if (type === 'customer') {
    customerIds.add(id);
    return;
  }
  if (type === 'odp') {
    toArray(indexes.customersByOdp.get(id)).forEach((item) => customerIds.add(customerIdOf(item)));
    return;
  }
  if (type === 'olt') {
    toArray(indexes.customersByOlt.get(id)).forEach((item) => customerIds.add(customerIdOf(item)));
    return;
  }
  if (type === 'nas' || type === 'router' || type === 'node') {
    toArray(indexes.customersByRouter.get(id)).forEach((item) => customerIds.add(customerIdOf(item)));
    return;
  }
  if (type === 'site') {
    toArray(indexes.customersBySite.get(id)).forEach((item) => customerIds.add(customerIdOf(item)));
  }
};

const deriveSeverity = ({ impacted, offline, degraded, warning, activeIncidentCount, activeTicketCount }) => {
  const score = (offline * 3) + (degraded * 2) + warning + (activeIncidentCount * 3) + (activeTicketCount * 2);
  if (offline >= 15 || (offline >= 5 && activeIncidentCount >= 2) || score >= 28) return 'critical';
  if (offline >= 3 || activeIncidentCount >= 1 || score >= 14) return 'high';
  if (impacted >= 3 || degraded > 0 || warning > 0 || activeTicketCount > 0 || score >= 5) return 'medium';
  return 'low';
};

const severityTone = (value) => {
  if (value === 'critical') return 'danger';
  if (value === 'high') return 'warn';
  if (value === 'medium') return 'info';
  return 'neutral';
};

const buildMitigationHint = ({ relations = [], siteSummary = null }) => {
  const relationItems = toArray(relations);
  const hasBackup = relationItems.some((item) => normalize(item?.relation_type) === 'backup' && item?.is_active !== false);
  const hasBalancing = relationItems.some((item) => normalize(item?.relation_type) === 'balancing' && item?.is_active !== false);
  if (hasBackup && hasBalancing) return 'Backup & balancing path tersedia. Mitigasi mungkin lebih cepat.';
  if (hasBackup) return 'Backup path available. Mitigasi mungkin mengurangi dampak.';
  if (hasBalancing) return 'Balancing path available. Cek distribusi trafik mitigasi.';
  if (siteSummary && siteSummary.activeIncidentCount > 0 && siteSummary.activeTicketCount > 0) return 'Incident dan ticket aktif terdeteksi. Mitigasi operasional perlu diprioritaskan.';
  return '';
};

const siteMessage = {
  unavailable: 'Dampak site belum bisa dihitung penuh.',
  empty: 'Belum ada dampak site yang terdeteksi.',
  partial: 'Relasi site belum lengkap.',
};

export const deriveSiteImpactSummary = ({
  context = {},
  data = {},
  relations = [],
  executionLogs = [],
  limit = 5,
}) => {
  const customers = toArray(data.customers);
  const incidents = toArray(data.incidents);
  const tickets = toArray(data.tickets);
  const odps = toArray(data.odps);
  const olts = toArray(data.olts);
  const nasRouters = toArray(data.nasRouters);
  const sites = toArray(data.sites);
  const siteCatalog = buildSiteCatalog(sites);

  if (!customers.length) {
    return {
      state: 'unavailable',
      message: siteMessage.unavailable,
      topSites: [],
      selectedSite: null,
      totals: {
        sitesImpacted: 0,
        impactedCustomers: 0,
        offline: 0,
        degraded: 0,
        warning: 0,
        unknown: 0,
        activeIncidentCount: 0,
        activeTicketCount: 0,
      },
    };
  }

  const indexes = buildIndexes({ customers, odps, olts, nasRouters, siteCatalog });
  const candidateCustomerIds = new Set();
  const activeIncidents = incidents.filter((incident) => !inactiveIncidentStatus.has(normalize(incident?.status)));
  const activeTickets = tickets.filter((ticket) => activeTicketStatus.has(normalize(ticket?.status)));
  const contextType = normalize(context.type || context.sourceType);
  const contextId = String(context.id || context.sourceId || '').trim();
  const contextSiteId = String(context.siteId || '').trim();
  const incidentId = String(context.incidentId || context.incident?.id || '').trim();
  const focusIncident = context.incident || activeIncidents.find((item) => String(item?.id || '').trim() === incidentId) || null;

  if (!contextType && !contextId && !contextSiteId && !focusIncident) {
    customers.forEach((customer) => {
      const state = classifyCustomerState(customerStatusRaw(customer));
      if (state !== 'healthy') candidateCustomerIds.add(customerIdOf(customer));
    });
  }

  if (focusIncident) {
    toArray(focusIncident.affectedCustomers).forEach((id) => {
      const token = String(id || '').trim();
      if (token) candidateCustomerIds.add(token);
    });
    toArray(focusIncident.impactedEntities || focusIncident.linkedEntityKeys).forEach((ref) => {
      addCustomersFromEntity({ entity: parseEntityRef(ref), indexes, customerIds: candidateCustomerIds });
    });
  }

  if (contextType && contextId) {
    addCustomersFromEntity({
      entity: {
        type: contextType === 'nas' ? 'router' : contextType,
        id: contextId,
      },
      indexes,
      customerIds: candidateCustomerIds,
    });
  }

  const ticketContexts = [
    { type: 'customer', id: context.customerId },
    { type: 'router', id: context.routerId || context.nodeId },
    { type: 'site', id: contextSiteId },
  ];
  ticketContexts.forEach((ctx) => {
    const id = String(ctx.id || '').trim();
    if (!id) return;
    addCustomersFromEntity({ entity: { type: ctx.type, id }, indexes, customerIds: candidateCustomerIds });
  });

  const impactedCustomers = Array.from(candidateCustomerIds)
    .map((id) => indexes.customersById.get(id))
    .filter(Boolean);

  if (!impactedCustomers.length) {
    const hasContext = Boolean(contextType || contextId || contextSiteId || focusIncident);
    return {
      state: hasContext ? 'partial' : 'empty',
      message: hasContext ? siteMessage.partial : siteMessage.empty,
      topSites: [],
      selectedSite: null,
      totals: {
        sitesImpacted: 0,
        impactedCustomers: 0,
        offline: 0,
        degraded: 0,
        warning: 0,
        unknown: 0,
        activeIncidentCount: 0,
        activeTicketCount: 0,
      },
    };
  }

  const siteMap = new Map();
  const ensureSite = (siteId) => {
    const token = String(siteId || '').trim();
    if (!token || token === '-') return null;
    if (!siteMap.has(token)) {
      siteMap.set(token, {
        site_id: token,
        site_name: token,
        impacted_customers_total: 0,
        offline_count: 0,
        degraded_count: 0,
        warning_count: 0,
        unknown_count: 0,
        active_incident_count: 0,
        active_ticket_count: 0,
        related_router_count: 0,
        severity: 'low',
        severityTone: 'neutral',
        top_customers: [],
        top_related_nodes: [],
        mitigation_hint: '',
        last_operational_update: null,
        _customerRows: [],
        _routerSet: new Set(),
      });
    }
    return siteMap.get(token);
  };

  impactedCustomers.forEach((customer) => {
    const customerId = customerIdOf(customer);
    const state = classifyCustomerState(customerStatusRaw(customer));
    const site = indexes.customerSiteById.get(customerId) || customerSiteOf(customer, siteCatalog) || '-';
    const row = ensureSite(site);
    if (!row) return;
    row.impacted_customers_total += 1;
    if (state === 'offline') row.offline_count += 1;
    else if (state === 'degraded') row.degraded_count += 1;
    else if (state === 'warning') row.warning_count += 1;
    else if (state === 'unknown') row.unknown_count += 1;

    const routerId = linkedRouterIdOf(customer);
    if (routerId) row._routerSet.add(routerId);
    row._customerRows.push({
      id: customerId,
      code: customerCodeOf(customer),
      name: customerNameOf(customer),
      state,
      site,
      status: customerStatusRaw(customer),
    });
  });

  const addIncidentToSite = (siteId) => {
    const row = ensureSite(siteId);
    if (row) row.active_incident_count += 1;
  };
  activeIncidents.forEach((incident) => {
    const directSite = String(incident?.site || incident?.site_id || '').trim();
    if (directSite) addIncidentToSite(directSite);
    toArray(incident?.impactedEntities || incident?.linkedEntityKeys).forEach((ref) => {
      const parsed = parseEntityRef(ref);
      if (!parsed) return;
      if (parsed.type === 'site') addIncidentToSite(parsed.id);
      if (parsed.type === 'customer') {
        const site = indexes.customerSiteById.get(parsed.id);
        if (site) addIncidentToSite(site);
      }
    });
  });

  const addTicketToSite = (siteId) => {
    const row = ensureSite(siteId);
    if (row) row.active_ticket_count += 1;
  };
  activeTickets.forEach((ticket) => {
    const directSite = String(ticket?.siteId || ticket?.site_id || '').trim();
    if (directSite) addTicketToSite(directSite);
    const sourceType = normalize(ticket?.sourceType || ticket?.source_type);
    const sourceId = String(ticket?.sourceId || ticket?.source_id || '').trim();
    if (sourceType === 'site' && sourceId) addTicketToSite(sourceId);
    const customerId = String(ticket?.customerId || ticket?.customer_id || '').trim();
    if (customerId) {
      const site = indexes.customerSiteById.get(customerId);
      if (site) addTicketToSite(site);
    }
  });

  const logs = toArray(executionLogs);
  logs.forEach((entry) => {
    const siteId = String(entry?.siteId || entry?.site_id || '').trim();
    const row = ensureSite(siteId);
    if (!row) return;
    const createdAt = String(entry?.createdAt || entry?.created_at || '').trim();
    if (!createdAt) return;
    if (!row.last_operational_update || String(row.last_operational_update.createdAt || '') < createdAt) {
      row.last_operational_update = {
        actionType: String(entry?.actionType || entry?.action_type || '').trim(),
        message: String(entry?.message || '').trim(),
        actor: String(entry?.actor || '-').trim(),
        createdAt,
      };
    }
  });

  const routerNameById = new Map();
  nasRouters.forEach((router) => {
    const id = String(router?.id || router?.router_id || '').trim();
    if (!id) return;
    routerNameById.set(id, String(router?.name || router?.nas_identifier || id));
  });

  const rankedSites = Array.from(siteMap.values()).map((site) => {
    const topCustomers = site._customerRows
      .sort((a, b) => {
        const rank = { offline: 0, degraded: 1, warning: 2, unknown: 3, healthy: 4 };
        const diff = (rank[a.state] ?? 9) - (rank[b.state] ?? 9);
        if (diff !== 0) return diff;
        return String(a.name || '').localeCompare(String(b.name || ''));
      })
      .slice(0, 5);

    const topNodes = sortByName(Array.from(site._routerSet)).slice(0, 5).map((id) => ({
      id,
      name: routerNameById.get(id) || id,
    }));

    const severity = deriveSeverity({
      impacted: site.impacted_customers_total,
      offline: site.offline_count,
      degraded: site.degraded_count,
      warning: site.warning_count,
      activeIncidentCount: site.active_incident_count,
      activeTicketCount: site.active_ticket_count,
    });

    return {
      ...site,
      related_router_count: site._routerSet.size,
      top_customers: topCustomers,
      top_related_nodes: topNodes,
      severity,
      severityTone: severityTone(severity),
    };
  });

  rankedSites.forEach((site) => {
    site.mitigation_hint = buildMitigationHint({
      relations: contextSiteId && site.site_id === contextSiteId ? relations : [],
      siteSummary: site,
    });
    delete site._customerRows;
    delete site._routerSet;
  });

  rankedSites.sort((a, b) => {
    const rank = { critical: 3, high: 2, medium: 1, low: 0 };
    const diff = (rank[b.severity] || 0) - (rank[a.severity] || 0);
    if (diff !== 0) return diff;
    const offlineDiff = Number(b.offline_count || 0) - Number(a.offline_count || 0);
    if (offlineDiff !== 0) return offlineDiff;
    return Number(b.impacted_customers_total || 0) - Number(a.impacted_customers_total || 0);
  });

  const selectedSite = rankedSites.find((item) => item.site_id === contextSiteId) || rankedSites[0] || null;
  const topSites = rankedSites.slice(0, Math.max(1, Number(limit || 5)));

  const totals = rankedSites.reduce((acc, site) => {
    acc.sitesImpacted += 1;
    acc.impactedCustomers += Number(site.impacted_customers_total || 0);
    acc.offline += Number(site.offline_count || 0);
    acc.degraded += Number(site.degraded_count || 0);
    acc.warning += Number(site.warning_count || 0);
    acc.unknown += Number(site.unknown_count || 0);
    acc.activeIncidentCount += Number(site.active_incident_count || 0);
    acc.activeTicketCount += Number(site.active_ticket_count || 0);
    return acc;
  }, {
    sitesImpacted: 0,
    impactedCustomers: 0,
    offline: 0,
    degraded: 0,
    warning: 0,
    unknown: 0,
    activeIncidentCount: 0,
    activeTicketCount: 0,
  });

  return {
    state: 'ok',
    message: '',
    topSites,
    selectedSite,
    totals,
  };
};
